import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Swords,
  Dumbbell,
  LineChart,
  BookOpen,
  Sparkles,
  Timer,
  Camera,
  Trophy,
  Heart,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Command", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/quests", label: "Quests", icon: Swords, testid: "nav-quests" },
  { to: "/workouts", label: "Training", icon: Dumbbell, testid: "nav-workouts" },
  { to: "/metrics", label: "Vitals", icon: Heart, testid: "nav-metrics" },
  { to: "/lesson", label: "Lesson", icon: Sparkles, testid: "nav-lesson" },
  { to: "/timer", label: "Focus", icon: Timer, testid: "nav-timer" },
  { to: "/journal", label: "Journal", icon: BookOpen, testid: "nav-journal" },
  { to: "/photos", label: "Progress", icon: Camera, testid: "nav-photos" },
  { to: "/analytics", label: "Analytics", icon: LineChart, testid: "nav-analytics" },
  { to: "/achievements", label: "Badges", icon: Trophy, testid: "nav-achievements" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const doLogout = () => {
    logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen relative">
      <div className="noise-overlay" />

      {/* Top bar - mobile */}
      <header className="lg:hidden sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border border-cyan-400/50 flex items-center justify-center">
              <span className="text-cyan-400 font-display font-bold">S</span>
            </div>
            <span className="font-display font-bold tracking-widest text-sm">SOLO ASCEND</span>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 border border-white/10 hover:border-cyan-400/50"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:sticky top-0 lg:top-0 left-0 z-30 w-64 h-screen border-r border-white/10 bg-black/60 backdrop-blur-xl transition-transform duration-200`}
        >
          <div className="p-6 border-b border-white/10 hidden lg:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-cyan-400/50 flex items-center justify-center">
                <span className="text-cyan-400 font-display font-bold text-lg">S</span>
              </div>
              <div>
                <div className="font-display font-bold tracking-widest">SOLO ASCEND</div>
                <div className="label-tech text-[0.55rem]">Hunter System</div>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="label-tech mb-3">Hunter</div>
            <div className="mb-1 font-display font-semibold text-lg truncate" data-testid="sidebar-username">
              {user?.name}
            </div>
            <div className="text-xs text-slate-400 mb-4">
              LVL <span className="text-cyan-400 font-mono-tech">{user?.level ?? 1}</span> ·{" "}
              <span className="uppercase text-violet-400">{(user?.avatar_stage || "e_rank").replace("_", " ")}</span>
            </div>
          </div>

          <nav className="px-3 flex-1 overflow-y-auto pb-24">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 mb-1 border transition-all font-display uppercase text-sm tracking-wider ${
                    isActive
                      ? "bg-cyan-400/10 border-cyan-400/60 text-cyan-300 shadow-[0_0_20px_-8px_rgba(0,229,255,0.5)]"
                      : "border-transparent text-slate-400 hover:text-white hover:border-white/10"
                  }`
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-black/40">
            <button
              data-testid="logout-button"
              onClick={doLogout}
              className="w-full flex items-center gap-2 px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 font-display uppercase text-xs tracking-widest"
            >
              <LogOut size={14} /> Log Out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 lg:ml-0 min-h-screen relative z-10">
          <div className="max-w-6xl mx-auto p-4 lg:p-8">{children}</div>
        </main>
      </div>

      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0F0F16",
            border: "1px solid rgba(0,229,255,0.3)",
            color: "#F8FAFC",
          },
        }}
      />
    </div>
  );
}
